import React, { useState, useEffect, useMemo, useContext } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { makeStyles } from "@mui/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../context";
import { useMutation } from "@tanstack/react-query";
import { signOutUserFn } from "../APIServices/Services";
import logo from "../../assets/Pivot-Club.jpg";
import Spinner from "./Spinner";
import { toast } from "react-toastify";

function ElevationScroll(props) {
  const { children } = props;

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

const useStyles = makeStyles((theme) => ({
  toolbarMargin: {
    ...theme.mixins.toolbar,
    marginBottom: "3em",
    [theme.breakpoints.down("lg")]: {
      marginBottom: "2em",
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: "1.25em",
    },
  },
  logo: {
    height: "8em",
    [theme.breakpoints.down("lg")]: {
      height: "7em",
    },
    [theme.breakpoints.down("sm")]: {
      height: "5.5em",
    },
    //width: 200,
  },
  logoContainer: {
    padding: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  tabContainer: {
    marginLeft: "auto",
  },
  tab: {
    ...theme.typography.tab,
    minWidth: 10,
    marginLeft: "25px",
  },
  button: {
    ...theme.typography.learnButton,
    borderRadius: "45px",
    marginLeft: "10px",
    marginRight: "15px",
    height: "45px",
    backgroundColor: theme.palette.common.orange,
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  menu: {
    backgroundColor: theme.palette.common.blue,
    color: "white",
    borderRadius: "0px",
  },
  menuItem: {
    ...theme.typography.tab,
    opacity: 0.7,
    "&:hover": {
      opacity: 1,
    },
  },
  drawerIcon: {
    height: "50px",
    width: "50px",
    color: "white",
  },
  drawerIconContainer: {
    marginLeft: "auto",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  drawer: {
    backgroundColor: theme.palette.common.blue,
  },
  drawerItem: {
    ...theme.typography.tab,
    color: "white",
    opacity: 0.7,
  },
  drawerItemEstimate: {
    backgroundColor: theme.palette.common.orange,
    marginBottom: "10px",
  },
  drawerItemSelected: {
    "& .MuiListItemText-root": {
      opacity: 1,
    },
  },
  appbar: {
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.common.blue,
  },
}));

export default function Header(props) {
  const navigate = useNavigate();
  const { authUser } = useStateContext();

  const { mutate: logoutUser, isLoading } = useMutation(
    async () => await signOutUserFn(),
    {
      onSuccess: (data) => {
        window.location.href = "/login";
      },
      onError: (error) => {
        if (Array.isArray(error.response.data.error)) {
          error.data.error.forEach((el) =>
            toast.error(el.message, {
              position: "top-right",
            })
          );
        } else {
          toast.error(error.response.data.message, {
            position: "top-right",
          });
        }
      },
    }
  );

  const onLogoutHandler = async () => {
    logoutUser();
  };

  const classes = useStyles();
  const theme = useTheme();
  //const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const [openDrawer, setOpenDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  const handleChange = (e, newValue) => {
    props.setValue(newValue);
  };

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
    setOpenMenu(true);
  };

  const handleMenuItemClick = (e, i) => {
    setAnchorEl(null);
    setOpenMenu(false);
    props.setSelectedIndex(i);
  };

  const handleClose = (e) => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const menuOptions = useMemo(() => {
    return [
      {
        name: "Activities",
        link: "/activities",
        activeIndex: 1,
        selectedIndex: 0,
      },
      {
        name: "Projects",
        link: "/projects",
        activeIndex: 1,
        selectedIndex: 1,
      },
      {
        name: "Research Papers",
        link: "/researchpapers",
        activeIndex: 1,
        selectedIndex: 2,
      },
      {
        name: "Events",
        link: "/events",
        activeIndex: 1,
        selectedIndex: 3,
      },
      {
        name: "Tech Flame",
        link: "/techflame",
        activeIndex: 1,
        selectedIndex: 4,
      },
    ];
  }, []);

  const routes = useMemo(() => {
    return [
      { name: "Home", link: "/", activeIndex: 0 },
      {
        name: "Activities",
        link: "/activities",
        activeIndex: 1,
        ariaOwns: anchorEl ? "simple-menu" : undefined,
        ariaPopup: anchorEl ? "true" : undefined,
        mouseOver: (event) => handleClick(event),
      },
      { name: "Our Teams", link: "/teams", activeIndex: 3 },
      { name: "About Us", link: "/about", activeIndex: 3 },
      { name: "Contact Us", link: "/contact", activeIndex: 4 },
    ];
  }, [anchorEl]);

  useEffect(() => {
    [...menuOptions, ...routes].forEach((route) => {
      switch (window.location.pathname) {
        case `${route.link}`:
          if (props.value !== route.activeIndex) {
            props.setValue(route.activeIndex);
            if (
              route.selectedIndex &&
              route.selectedIndex !== props.selectedIndex
            ) {
              props.setSelectedIndex(route.selectedIndex);
            }
          }
          break;
        default:
          break;
      }
    });
  }, [props.value, menuOptions, props.selectedIndex, routes, props]);

  const tabs = (
    <React.Fragment>
      <Tabs
        value={props.value}
        onChange={handleChange}
        className={classes.tabContainer}
        indicatorColor="primary"
      >
        {routes.map((route, index) => (
          <Tab
            key={`${route}${index}`}
            className={classes.tab}
            component={Link}
            to={route.link}
            label={route.name}
            aria-owns={route.ariaOwns}
            aria-haspopup={route.ariaPopup}
            onMouseOver={route.mouseOver}
          />
        ))}
      </Tabs>
      {!authUser && (
        <>
          <Link to="/login">
            <Button
              variant="contained"
              className={classes.button}
              onClick={() => props.setValue(5)}
            >
              Login
            </Button>
          </Link>
          <Link to="/register">
            <Button
              variant="contained"
              className={classes.button}
              onClick={() => props.setValue(6)}
            >
              Register
            </Button>
          </Link>
        </>
      )}
      {authUser && (
        <>
          <Button
            variant="contained"
            className={classes.button}
            onClick={() => navigate("/profile")}
          >
            Profile
          </Button>

          <Button
            variant="contained"
            className={classes.button}
            onClick={onLogoutHandler}
          >
            Logout
          </Button>
          {isLoading && <Spinner />}
        </>
      )}

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        classes={{ paper: classes.menu }}
        MenuListProps={{
          onMouseLeave: handleClose,
        }}
        elevation={0}
        style={{ zIndex: 1302 }}
        keepMounted
      >
        {menuOptions.map((option, i) => (
          <MenuItem
            key={`${option}${i}`}
            component={Link}
            to={option.link}
            classes={{ root: classes.menuItem }}
            onClick={(event) => {
              if (option.activeIndex === 1) {
                handleMenuItemClick(event, i);
                props.setValue(1);
                handleClose();
              } else if (option.activeIndex === 2) {
                handleMenuItemClick(event, i);
                props.setValue(2);
                handleClose();
              }
            }}
            selected={i === props.selectedIndex && props.value === 1}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );

  const drawer = (
    <React.Fragment>
      <SwipeableDrawer
        //disableBackdropTransition={!iOS}
        //disableDiscovery={iOS}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        classes={{ paper: classes.drawer }}
      >
        <div className={classes.toolbarMargin} />
        <List disablePadding>
          {routes.map((route) => (
            <ListItem
              divider
              key={`${route}${route.activeIndex}`}
              button
              component={Link}
              to={route.link}
              selected={props.value === route.activeIndex}
              classes={{ selected: classes.drawerItemSelected }}
              onClick={() => {
                setOpenDrawer(false);
                props.setValue(route.activeIndex);
              }}
            >
              <ListItemText className={classes.drawerItem} disableTypography>
                {route.name}
              </ListItemText>
            </ListItem>
          ))}
          {!authUser && (
            <>
              {" "}
              <ListItem
                onClick={() => {
                  setOpenDrawer(false);
                  props.setValue(5);
                }}
                divider
                button
                component={Link}
                classes={{
                  root: classes.drawerItemEstimate,
                  selected: classes.drawerItemSelected,
                }}
                to="/login"
                selected={props.value === 5}
              >
                <ListItemText className={classes.drawerItem} disableTypography>
                  Login
                </ListItemText>
              </ListItem>
              <ListItem
                onClick={() => {
                  setOpenDrawer(false);
                  props.setValue(6);
                }}
                divider
                button
                component={Link}
                classes={{
                  root: classes.drawerItemEstimate,
                  selected: classes.drawerItemSelected,
                }}
                to="/register"
                selected={props.value === 6}
              >
                <ListItemText className={classes.drawerItem} disableTypography>
                  Sign Up
                </ListItemText>
              </ListItem>
            </>
          )}
          {authUser && (
            <>
              <ListItem
                onClick={() => {
                  setOpenDrawer(false);
                  props.setValue(5);
                }}
                divider
                button
                component={Link}
                classes={{
                  root: classes.drawerItemEstimate,
                  selected: classes.drawerItemSelected,
                }}
                to="/profile"
                selected={props.value === 5}
              >
                <ListItemText className={classes.drawerItem} disableTypography>
                  Profile
                </ListItemText>
              </ListItem>

              <ListItem
                onClick={() => {
                  setOpenDrawer(false);
                  props.setValue(6);
                  onLogoutHandler();
                }}
                divider
                button
                component={Link}
                classes={{
                  root: classes.drawerItemEstimate,
                  selected: classes.drawerItemSelected,
                }}
                selected={props.value === 6}
              >
                <ListItemText className={classes.drawerItem} disableTypography>
                  Logout
                </ListItemText>
              </ListItem>
            </>
          )}
        </List>
      </SwipeableDrawer>
      <IconButton
        className={classes.drawerIconContainer}
        onClick={() => setOpenDrawer(!openDrawer)}
        disableRipple
        size="large"
      >
        <MenuIcon className={classes.drawerIcon} />
      </IconButton>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <ElevationScroll>
        <AppBar position="fixed" className={classes.appbar}>
          <Toolbar disableGutters>
            <Button
              component={Link}
              to="/"
              disableRipple
              onClick={() => props.setValue(0)}
              className={classes.logoContainer}
            >
              <img alt="company logo" className={classes.logo} src={logo} />
            </Button>
            {matches ? drawer : tabs}
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <div className={classes.toolbarMargin} />
    </React.Fragment>
  );
}
